// @ts-strict-ignore
import * as React from 'react';
import { ModalProps } from 'antd/lib/modal';
import Modal from 'antd/lib/modal/Modal';
import { flatMap } from 'lodash';
import { validatePattern } from '../helpers';
import useForm from 'antd/lib/form/hooks/useForm';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import { DisallowedTokensForTag, RequireAtLeastOneOfTokensForTag } from '../types';
import Button from 'antd/lib/button';
import Alert from 'antd/lib/alert';
import Spin from 'antd/lib/spin';
import { runwayColors } from './styles/globalStyles';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface NewReleaseTagPatternModalProps {
    open: boolean;
    isLoading: boolean;
    onCancelAddNewReleaseTagPattern: ModalProps['onCancel'];
    onOkNewReleaseTagPattern: (pattern: string) => void;
    suggestedTagPattern: string;
    suggestedTagPatternIsLoading: boolean;
}

const NewReleaseTagPatternModal = (props: NewReleaseTagPatternModalProps) => {
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();

    const [shouldDisplaySuggestedTagLabel, setShouldDisplaySuggestedTagLabel] = React.useState(false);

    const [shouldDisplaySuggestedTagSpinner, setShouldDisplaySuggestedTagSpinner] = React.useState(false);

    React.useEffect(() => {
        if (props.suggestedTagPattern !== '') {
            setShouldDisplaySuggestedTagLabel(true);
        }
    }, [props.suggestedTagPattern]);

    React.useEffect(() => {
        setShouldDisplaySuggestedTagSpinner(props.suggestedTagPatternIsLoading);
    }, [props.suggestedTagPatternIsLoading]);

    const onOk = () => {
        form.validateFields()
            .then((values) => {
                const selectedPattern = values.tagPattern;

                props.onOkNewReleaseTagPattern(selectedPattern);
            })
            .catch(() => setFormIsValid(false));
    };

    const onCancel = (e) => {
        props.onCancelAddNewReleaseTagPattern(e);
    };

    const onFieldsChange = () => {
        const allTouched = form.isFieldsTouched(true);
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        setFormIsValid(errors.length === 0 && allTouched);
    };

    const onApplySuggestionButton = () => {
        form.setFieldsValue({ tagPattern: props.suggestedTagPattern });
        onFieldsChange();
    };

    return (
        <Modal
            title="New release tag pattern"
            open={props.open}
            onCancel={onCancel}
            onOk={onOk}
            okText="Create tag pattern"
            confirmLoading={props.isLoading}
            okButtonProps={{ loading: props.isLoading, disabled: !formIsValid }}
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
            }}
        >
            <Form
                form={form}
                name="newReleaseTagPattern"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16, offset: 1 }}
                labelAlign="left"
                autoComplete="off"
                onFieldsChange={onFieldsChange}
            >
                <FormItem
                    label="Tag pattern"
                    name="tagPattern"
                    rules={[
                        { required: true },
                        {
                            validator: (rule, value) => {
                                const pattern = value as string;

                                return validatePattern(
                                    pattern,
                                    'Tag',
                                    RequireAtLeastOneOfTokensForTag,
                                    DisallowedTokensForTag,
                                );
                            },
                        },
                    ]}
                >
                    <Input placeholder="e.g. v{version}" autoComplete="off" />
                </FormItem>
                <Text type="secondary">
                    Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </Text>
                {shouldDisplaySuggestedTagSpinner && (
                    <p>
                        <Spin size="small" />
                    </p>
                )}
                {shouldDisplaySuggestedTagLabel && (
                    <Alert
                        style={{
                            marginTop: '1em',
                            marginBottom: '1em',
                            backgroundColor: `${runwayColors.primary}10`,
                            border: `1px solid ${runwayColors.primary}50`,
                        }}
                        message={
                            <>
                                Suggested:{' '}
                                <Text code style={{ margin: '1em' }}>
                                    {props.suggestedTagPattern}
                                </Text>
                                <TooltipInfoIcon tooltipContent="Suggestion for this field based on the tags we found in your repository" />
                            </>
                        }
                        action={
                            <Button size="small" onClick={onApplySuggestionButton}>
                                Apply
                            </Button>
                        }
                    />
                )}
            </Form>
        </Modal>
    );
};

export default NewReleaseTagPatternModal;
